import { PatientCountFiltersObject } from '@/features/patientAssignment';

import {
	FilterValuesObj,
	fetchSearchDataType,
	DataTableStrObject,
	fetchOrderingDataType,
	DataTableSimplifiedObject,
} from '../types';

type FilterNameObj = {
	id: string;
	name: string;
};

type FiltersArrObj = {
	[key: string]:
		| string
		| string[]
		| number
		| number[]
		| ((filterValue: string) => string | boolean)
		| undefined;
	formatting?: (filterValue: string | boolean) => string;
};

const formatParam = (filterValue: string | boolean | FilterValuesObj) => {
	let filterString;
	// check if the filterValue is an array and create a new array formatObjectValues by mapping through the fiterValue array
	// If an item in the array is an object, the function extracts its id property and pushes it into the new array. If it's not an object, it just pushes the item into the new array as is.
	if (Array.isArray(filterValue)) {
		const formatObjectValues = filterValue.map((item) => {
			if (typeof item === 'object') {
				return (item as FilterNameObj).id;
			}
			return item;
		});
		// joins the values in formatObjectValues into a string separated by commas and assigns the resulting string to filterString.
		filterString = formatObjectValues.join();
	} else {
		// if the filterValues variable is not an array, it assigns its value to filterString.
		filterString = filterValue;
	}
	return filterString;
};

export const buildFilterURL = (filtersArr: FiltersArrObj[]) => {
	// this function takes an array of filter objects and constructs the URL parameters based on each filter in the array.
	let filtersURL = '';
	filtersArr.forEach((filter) => {
		if (filter) {
			const filterArr = Object.entries(filter);
			if (filterArr && filterArr.length) {
				const [key, val] = filterArr[0];
				// default formatting function
				let formattingFunction = formatParam as (filterValue: string | boolean) => string;
				if (filterArr.length > 1) {
					const [optionsKey, filterFormattingFunction] = filterArr[1];
					if (
						optionsKey === 'formatting' &&
						typeof filterFormattingFunction === 'function'
					)
						// filter contains custom formatting function so use this instead
						formattingFunction = filterFormattingFunction as (
							filterValue: string | boolean
						) => string;
				}
				// append parameter to URL
				filtersURL += `&${key}=${val ? formattingFunction(val as string | boolean) : ''}`;
			}
		}
	});
	return filtersURL;
};

export const getPaginationQuery = (
	page: number | undefined,
	pageSize: string | number | undefined,
	getAll?: boolean
) => {
	return getAll ? `&all=true` : `&page=${page}&page_size=${pageSize}`;
};

export const valueHasFilterApplied = (
	value: string | string[] | boolean | FilterValuesObj | PatientCountFiltersObject
) => {
	// check if the given value actually has a value
	return (value as string | string[])?.length > 0 || (value as boolean) === true;
};

export const getActiveFilters = (filterValues: FilterValuesObj, parseFilterField: string[]) => {
	// returns an object of filters that only have a value
	const activeFilters = { ...filterValues };
	// parse any required fields first as some values can be stored as a JSON string
	if (parseFilterField?.length) {
		parseFilterField.forEach((filterName: string) => {
			const thisFilter = activeFilters[filterName];
			if (thisFilter) {
				activeFilters[filterName] = Array.isArray(thisFilter)
					? thisFilter.map((f) => JSON.parse(f))
					: typeof thisFilter === 'string'
						? JSON.parse(thisFilter as string)
						: thisFilter;
			}
		});
	}
	return Object.fromEntries(
		Object.entries(activeFilters).filter(([, value]) => valueHasFilterApplied(value))
	);
};

export const isFiltersApplied = (filterValues: FilterValuesObj) => {
	return Object.values(filterValues).filter((elm) => valueHasFilterApplied(elm)).length > 0;
};

export const getNewOrderType = (orderColumn: string, orderType: string, columnId: string) => {
	return columnId != orderColumn
		? 'asc'
		: orderType === 'default' || orderType === 'asc'
			? orderType === 'default'
				? 'asc'
				: 'desc'
			: 'default';
};

export const getNewOrdering = (orderType: string, columnId: string) => {
	return orderType === 'asc' || orderType === 'desc'
		? `${orderType === 'desc' ? '-' : ''}${columnId}`
		: '';
};

export const clientOrdering = (
	data: fetchOrderingDataType,
	orderValue: string,
	clientOrderingMapping?: { [key: string]: string }
) => {
	// order the data results based on the providedorder value
	if (!data || !data.results) return data;
	const orderValueClean = orderValue.replace('-', '');
	const orderingMapping = clientOrderingMapping || {};
	const orderValueName = orderingMapping[orderValueClean] || orderValueClean;
	const isDesc = orderValue.startsWith('-');

	const sortData = (data: DataTableSimplifiedObject[]) => {
		return [...data].sort((a: DataTableSimplifiedObject, b: DataTableSimplifiedObject) => {
			const aValue = a[orderValueName];
			const bValue = b[orderValueName];
			if (aValue === undefined || aValue === null) return 1;
			if (bValue === undefined || bValue === null) return -1;
			if (typeof aValue === 'string' && typeof bValue === 'string') {
				return isDesc ? bValue.localeCompare(aValue) : aValue.localeCompare(bValue);
			}
			if (typeof aValue === 'number' && typeof bValue === 'number') {
				return isDesc ? bValue - aValue : aValue - bValue;
			}
			return 0;
		});
	};
	const orderedResults = sortData(data.results);
	// we need to deep clone the results as the original data can't be mutated
	const orderedResultsClone: DataTableSimplifiedObject[] = JSON.parse(
		JSON.stringify(orderedResults)
	);
	// sort any child data as well
	orderedResultsClone.forEach((item) => {
		if (item.children && Array.isArray(item.children)) {
			item.children = sortData(item.children as DataTableSimplifiedObject[]);
		}
	});
	return {
		...data,
		results: orderedResultsClone,
	};
};

export const clientSearch = (
	data: fetchSearchDataType,
	searchValue: string,
	clientSearchProps?: string[]
) => {
	// filter the data results based on the provided search value
	if (!data || !data.results) return data;
	const searchValueLower = searchValue.toLowerCase();
	const searchProps = clientSearchProps || [];

	const searchDataItem = (item: DataTableStrObject, propParts: string[]) => {
		const itemValue =
			propParts.length === 1
				? item[propParts[0]]
				: (item[propParts[0]] as DataTableStrObject)[propParts[1]];
		// check if the item value contains the search value using case insensitive comparison
		if (itemValue && typeof itemValue === 'string') {
			return itemValue.toLowerCase().includes(searchValueLower);
		}
		return false;
	};

	const filteredResults = data.results.filter((item: DataTableStrObject) => {
		return searchProps.some((prop) => {
			// get the value for each search property
			// search prop can be a nested property e.g. 'patient.first_name', so may need to split the prop by '.' to get the value
			const propParts: string[] = prop.split('.');
			if (propParts.length === 0) return false;
			// search the top level data items
			const parentItemRes = searchDataItem(item, propParts);
			if (parentItemRes) return true;
			// if the data item has children then search those too
			if (item.children && Array.isArray(item.children)) {
				return (item.children as DataTableStrObject[]).some((childItem) =>
					searchDataItem(childItem, propParts)
				);
			}
			return false;
		});
	});

	// remove children that don't match the search if the parent also doesn't match
	// we need to deep clone the filtered results as the original data can't be mutated
	const filteredResultsClone: DataTableStrObject[] = JSON.parse(JSON.stringify(filteredResults));
	filteredResultsClone.forEach((item) => {
		// check if parent item matches the search
		const parentMatches = searchProps.some((prop) => {
			const propParts: string[] = prop.split('.');
			return searchDataItem(item, propParts);
		});
		if (parentMatches) return;
		// if the parent item doesn't match the search then remove all children that don't match
		if (item.children && Array.isArray(item.children)) {
			item.children = (item.children as DataTableStrObject[]).filter((childItem) => {
				// check if child item matches the search
				const childMatches = searchProps.some((prop) => {
					const propParts: string[] = prop.split('.');
					return searchDataItem(childItem, propParts);
				});
				return childMatches;
			});
		}
	});

	return {
		...data,
		results: filteredResultsClone,
		total: filteredResultsClone.length,
	};
};
